import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { useActiveChannelId, useHasPermission } from '../../store/index.js'
import { useProgramDetailById, useProgramActions } from '../../hooks/usePrograms.js'
import { programsApi, antennaElementTypesApi } from '../../lib/api.js'
import type { AntennaElementType, Program } from '../../types/index.js'
import { ProgramForm }       from '../programs/ProgramForm.js'
import { MediaAssetManager } from '../programs/MediaAssetManager.js'
import { ProgramSidebar }    from '../programs/ProgramSidebar.js'
import { ANTENNA_ELEMENT_PROGRAM_TYPES, antennaElementTypeLabel, formatDurationShort } from '../../lib/utils.js'
import { PageHeader, PageTabs, type PageTab } from '../common/PageChrome.js'
import { ResizeHandle } from '../common/ResizeHandle.js'
import { ModalShell } from '../common/ModalShell.js'
import { JinglesOverview } from './JinglesOverview.js'
import { AntennaElementTypesPanel } from './AntennaElementTypesPanel.js'
import { Button } from '../ui/button.js'
import { Checkbox } from '../ui/checkbox.js'
import { updateRangeSelection } from '../../lib/range-selection.js'
import '../programs/ProgramsView.css'
import { Spinner } from '../common/Spinner.js'
import './JinglesView.css'

// ─── Poignée de redimensionnement ─────────────────────────────────────────────

// ─── Composant racine ─────────────────────────────────────────────────────────

const MIN_W = 140
const MAX_W = 560

type JinglesTab = 'elements' | 'types'

const JINGLES_TABS: readonly PageTab[] = [
  { id: 'elements', label: 'Éléments' },
  { id: 'types',    label: 'Types' },
]

/**
 * Un élément d'antenne porte à la fois l'enum technique (clé du moteur) et,
 * depuis la refonte, un type éditorial renommable. Les fiches créées avant la
 * migration peuvent n'avoir que l'enum : on rattache alors la ligne au type
 * d'origine qui porte le même code.
 */
function matchesElementType(program: Program, elementType: AntennaElementType): boolean {
  return program.antennaElementTypeId
    ? program.antennaElementTypeId === elementType.id
    : elementType.code === program.type
}

interface JinglesViewProps {
  onAddToGrid?: (program: Program) => void
}

export const JinglesView = memo(function JinglesView({ onAddToGrid }: JinglesViewProps = {}) {
  const activeChannelId = useActiveChannelId()
  const canManage = useHasPermission('JINGLE_MANAGE')
  const canAddToGrid = useHasPermission('GRID_ITEM_CREATE')

  const [jingles,      setJingles]      = useState<Program[]>([])
  const [loading,      setLoading]      = useState(false)
  const [search,       setSearch]       = useState('')
  const [typeFilter,   setTypeFilter]   = useState<string>('all')
  const [elementTypes, setElementTypes] = useState<AntennaElementType[]>([])
  const [tab,          setTab]          = useState<JinglesTab>('elements')
  const [selectedId,   setSelectedId]   = useState<string | null>(null)
  const [showNewModal, setShowNewModal] = useState(false)
  const [leftWidth,    setLeftWidth]    = useState(264)
  const [rightWidth,   setRightWidth]   = useState(264)

  const { detail: program, reload: reloadDetail } = useProgramDetailById(selectedId)

  // ── Chargement des jingles ───────────────────────────────────────────────
  const loadJingles = useCallback(async () => {
    if (!activeChannelId) return
    setLoading(true)
    try {
      const responses = await Promise.all(
        ANTENNA_ELEMENT_PROGRAM_TYPES.map(type =>
          programsApi.list({ channelId: activeChannelId, type, limit: 100, sortBy: 'title', sortDir: 'asc' })
            .catch(() => ({ data: [] })),
        ),
      )
      const map = new Map<string, Program>()
      responses.forEach(res => {
        (res?.data ?? []).forEach((p: Program) => map.set(p.id, p))
      })
      const merged = Array.from(map.values())
        .sort((a: Program, b: Program) => a.title.localeCompare(b.title, 'fr'))
      setJingles(merged)
    } catch {
      setJingles([])
    } finally {
      setLoading(false)
    }
  }, [activeChannelId])

  useEffect(() => { loadJingles() }, [loadJingles])

  const loadElementTypes = useCallback(() => {
    if (!activeChannelId) { setElementTypes([]); return }
    antennaElementTypesApi.list({ channelId: activeChannelId })
      .then(setElementTypes)
      .catch(() => setElementTypes([]))
  }, [activeChannelId])

  useEffect(() => { loadElementTypes() }, [loadElementTypes])

  const handleTypesChanged = useCallback(() => { loadElementTypes(); loadJingles() }, [loadElementTypes, loadJingles])
  const handleTabChange = useCallback((id: string) => setTab(id as JinglesTab), [])

  const handleSelect  = useCallback((p: Program) => setSelectedId(p.id), [])
  const handleSaved   = useCallback((_updated: Program) => { reloadDetail(); loadJingles() }, [reloadDetail, loadJingles])
  const handleArchive = useCallback((_id: string) => { setSelectedId(null); loadJingles() }, [loadJingles])
  const handleCreated = useCallback((p: Program) => { setShowNewModal(false); setSelectedId(p.id); loadJingles() }, [loadJingles])
  const handleOpenNew = useCallback(() => setShowNewModal(true), [])
  const handleCloseNew = useCallback(() => setShowNewModal(false), [])
  const handleDeleteMany = useCallback(async (ids: string[]) => {
    const results = await Promise.allSettled(ids.map(id => programsApi.deleteFromCatalog(id)))
    const deletedIds = ids.filter((_, index) => results[index]?.status === 'fulfilled')
    if (selectedId && deletedIds.includes(selectedId)) setSelectedId(null)
    await loadJingles()
    const failed = ids.length - deletedIds.length
    if (failed > 0) alert(`${failed} élément${failed > 1 ? 's' : ''} n'ont pas pu être supprimé${failed > 1 ? 's' : ''}. Ils sont peut-être encore référencés dans une grille, une récurrence ou un modèle.`)
    return deletedIds
  }, [selectedId, loadJingles])

  const onLeftResize  = useCallback((delta: number) => setLeftWidth(w  => Math.min(MAX_W, Math.max(MIN_W, w  + delta))), [])
  const onRightResize = useCallback((delta: number) => setRightWidth(w => Math.min(MAX_W, Math.max(MIN_W, w  - delta))), [])

  // ── Filtrage local ───────────────────────────────────────────────────────
  const activeTypeFilter = elementTypes.find(t => t.id === typeFilter)
  const filtered = jingles.filter(p => {
    const matchType   = typeFilter === 'all' || (activeTypeFilter ? matchesElementType(p, activeTypeFilter) : false)
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase())
    return matchType && matchSearch
  })

  return (
    <div className="tp-page-shell jingles-view">

      {/* Topbar */}
      <PageHeader
        title="Éléments d’antenne"
        actions={tab === 'elements' ? (
          <button
            type="button"
            onClick={handleOpenNew}
            disabled={!activeChannelId || !canManage}
            title={!canManage ? 'Permission de gestion des éléments d’antenne requise' : undefined}
            className="tp-button tp-button--primary"
            aria-label="Nouvel élément d’antenne"
          >
            + Nouveau
          </button>
        ) : undefined}
      />
      <PageTabs
        items={JINGLES_TABS}
        activeId={tab}
        onChange={handleTabChange}
        ariaLabel="Sections des éléments d’antenne"
        controlsId="jingles-panel"
        idPrefix="jingles-tab"
      />

      {/* Layout 3 colonnes redimensionnables */}
      {tab === 'types' ? (
        <div
          id="jingles-panel"
          className="jingles-types-pane"
          role="tabpanel"
          aria-labelledby="jingles-tab-types"
        >
          {activeChannelId
            ? <AntennaElementTypesPanel channelId={activeChannelId} canManage={canManage} onChanged={handleTypesChanged} />
            : <div className="catalog-list"><div className="catalog-empty">Sélectionnez une chaîne pour afficher ses types d’élément d’antenne.</div></div>
          }
        </div>
      ) : (
      <div
        id="jingles-panel"
        className={`jingles-workspace${program ? ' has-selection' : ''}`}
        role="tabpanel"
        aria-labelledby="jingles-tab-elements"
      >

        {/* Colonne gauche — liste */}
        <aside
          className="jingles-list-pane"
          aria-label="Catalogue des éléments d’antenne"
          style={{ '--jingles-list-width': `${leftWidth}px` } as React.CSSProperties}
        >
          <JingleList
            jingles={filtered}
            loading={loading}
            search={search}
            typeFilter={typeFilter}
            elementTypes={elementTypes}
            selectedId={selectedId}
            onSearch={setSearch}
            onTypeFilter={setTypeFilter}
            onSelect={handleSelect}
            onDeleteMany={handleDeleteMany}
            canManage={canManage}
          />
        </aside>

        <ResizeHandle label="Redimensionner le catalogue des éléments d’antenne" onDelta={onLeftResize} />

        {/* Colonne centrale */}
        <main className="jingles-detail-pane">
          {program ? (
            <div className="jingles-detail-scroll">
              <div className="jingles-detail-mobile-bar">
                <button type="button" className="tp-button" onClick={() => setSelectedId(null)}>← Catalogue</button>
              </div>
              <ProgramForm program={program} onSaved={handleSaved} onArchive={handleArchive} mode="jingle" />
              <fieldset
                className="catalog-permission-fieldset"
                disabled={!canManage}
                title={!canManage ? 'Consultation seule — permission de gestion des éléments d’antenne requise' : undefined}
              >
                <MediaAssetManager program={program} />
              </fieldset>
            </div>
          ) : (
            /* Comme sur Programmes : le centre ne reste pas vide tant que rien
               n'est sélectionné. */
            <div className="jingles-detail-scroll">
              <JinglesOverview jingles={jingles} elementTypes={elementTypes} loading={loading} onSelect={handleSelect} />
            </div>
          )}
        </main>

        <ResizeHandle label="Redimensionner le panneau d’informations" onDelta={onRightResize} />

        {/* Colonne droite — statistiques */}
        <aside
          className="jingles-meta-pane"
          aria-label="Informations de l’élément d’antenne"
          style={{ '--jingles-meta-width': `${rightWidth}px` } as React.CSSProperties}
        >
          {program
            ? <ProgramSidebar program={program} onAddToGrid={canAddToGrid ? onAddToGrid : undefined} />
            : <div className="jingles-meta-pane__empty" />
          }
        </aside>
      </div>
      )}

      {showNewModal && activeChannelId && canManage && (
        <NewJingleModal
          channelId={activeChannelId}
          elementTypes={elementTypes}
          onCreated={handleCreated}
          onClose={handleCloseNew}
        />
      )}
    </div>
  )
})

// ─── Liste des jingles ────────────────────────────────────────────────────────

interface JingleListProps {
  jingles:      Program[]
  loading:      boolean
  search:       string
  typeFilter:   string
  elementTypes: AntennaElementType[]
  selectedId:   string | null
  onSearch:       (s: string) => void
  onTypeFilter:   (t: string) => void
  onSelect:       (p: Program) => void
  onDeleteMany:   (ids: string[]) => Promise<string[]>
  canManage:      boolean
}

const JingleList = memo(function JingleList({ jingles, loading, search, typeFilter, elementTypes, selectedId, onSearch, onTypeFilter, onSelect, onDeleteMany, canManage }: JingleListProps) {
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [deleting, setDeleting] = useState(false)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  const selectionAnchorRef = useRef<string | null>(null)
  const jingleIds = jingles.map(jingle => jingle.id)

  useEffect(() => {
    if (!showFilterMenu) return
    const onClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) setShowFilterMenu(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [showFilterMenu])

  useEffect(() => {
    const visibleIds = new Set(jingleIds)
    setSelectedIds(previous => {
      const next = new Set([...previous].filter(id => visibleIds.has(id)))
      return next.size === previous.size ? previous : next
    })
    if (selectionAnchorRef.current && !visibleIds.has(selectionAnchorRef.current)) {
      selectionAnchorRef.current = null
    }
  }, [jingles])

  const handleSelect = useCallback((program: Program) => {
    onSelect(program)
  }, [onSelect])

  const handleCheck = useCallback((program: Program, checked: boolean, shiftKey: boolean) => {
    setSelectedIds(previous => shiftKey
      ? updateRangeSelection(jingleIds, previous, selectionAnchorRef.current, program.id, checked)
      : (() => {
          const next = new Set(previous)
          if (checked) next.add(program.id)
          else next.delete(program.id)
          return next
        })())
    if (!shiftKey) selectionAnchorRef.current = program.id
  }, [jingleIds])

  const allSelected = jingleIds.length > 0 && jingleIds.every(id => selectedIds.has(id))
  const someSelected = jingleIds.some(id => selectedIds.has(id))

  const handleCheckAll = useCallback(() => {
    setSelectedIds(allSelected ? new Set() : new Set(jingleIds))
    selectionAnchorRef.current = null
  }, [allSelected, jingleIds])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Supprimer définitivement ${selectedIds.size} élément${selectedIds.size > 1 ? 's' : ''} d’antenne du catalogue ?\n\nLes fichiers médias resteront sur le disque.`)) return
    setDeleting(true)
    try {
      const deletedIds = await onDeleteMany([...selectedIds])
      setSelectedIds(previous => {
        const next = new Set(previous)
        deletedIds.forEach(id => next.delete(id))
        return next
      })
      selectionAnchorRef.current = null
    } finally {
      setDeleting(false)
    }
  }, [selectedIds, onDeleteMany])

  return (
    <div className="jingle-list">

      {/* Même barre que le catalogue Programmes : champ pleine largeur et
          filtres dans un popover, plutôt que sept pastilles qui débordaient de
          la colonne. */}
      <div className="catalog-toolbar" ref={filterMenuRef}>
        <div className="catalog-search-wrapper">
          <button
            type="button"
            className={`catalog-filter-toggle-btn${typeFilter !== 'all' ? ' is-active' : ''}`}
            onClick={() => setShowFilterMenu(prev => !prev)}
            title="Filtrer par type"
            aria-label="Filtrer par type"
            aria-expanded={showFilterMenu}
          >
            <svg className="catalog-filter-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {typeFilter !== 'all' && <span className="catalog-filter-badge-dot" />}
          </button>

          <input
            type="text"
            placeholder="Rechercher un élément d’antenne…"
            value={search}
            onChange={e => onSearch(e.target.value)}
            className="tp-input catalog-search catalog-search-with-filter-btn"
            aria-label="Rechercher un élément d’antenne"
          />

          {showFilterMenu && (
            <div className="catalog-filter-popover" role="dialog" aria-label="Filtrer par type">
              <div className="catalog-filter-popover__header">
                <span>Type d’élément d’antenne</span>
                <button type="button" onClick={() => setShowFilterMenu(false)} className="catalog-filter-popover__close" aria-label="Fermer">×</button>
              </div>
              <div className="catalog-filter-popover__body">
                <label className="tp-field-label" htmlFor="jingle-type-filter">Type</label>
                <select
                  id="jingle-type-filter"
                  className="tp-select"
                  value={typeFilter}
                  onChange={e => { onTypeFilter(e.target.value); setShowFilterMenu(false) }}
                >
                  <option value="all">Tous</option>
                  {elementTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compteur */}
      <div className="jingle-list__count">
        <Checkbox
          className="catalog-selection-checkbox"
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          disabled={!canManage || loading || jingles.length === 0}
          onClick={handleCheckAll}
          aria-label="Sélectionner tous les éléments d’antenne affichés"
        />
        <span role="status">
          {loading
            ? <><Spinner /> Chargement…</>
            : `${selectedIds.size > 0 ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''} · ` : ''}${jingles.length} élément${jingles.length > 1 ? 's' : ''}`}
        </span>
        {selectedIds.size > 0 && (
          <Button
            type="button"
            variant="destructive"
            size="xs"
            className="jingle-list__bulk-delete"
            disabled={!canManage || deleting}
            onClick={() => void handleBulkDelete()}
          >
            {deleting ? 'Suppression…' : `Supprimer (${selectedIds.size})`}
          </Button>
        )}
      </div>

      {/* Liste */}
      <div className="jingle-list__rows">
        {jingles.map(p => (
          <JingleRow
            key={p.id}
            program={p}
            active={selectedId === p.id}
            checked={selectedIds.has(p.id)}
            canManage={canManage}
            onSelect={handleSelect}
            onCheck={handleCheck}
          />
        ))}
        {!loading && jingles.length === 0 && (
          <div className="tp-empty-state jingle-list__empty">
            Aucun élément d’antenne trouvé
          </div>
        )}
      </div>
    </div>
  )
})

// ─── Icônes vidéo ────────────────────────────────────────────────────────────

/** Pellicule + note de musique — pour les jingles */
const IconJingle = memo(function IconJingle() {
  return (
    <svg aria-hidden="true" width="22" height="18" viewBox="0 0 22 18" fill="none">
      {/* Pellicule */}
      <rect x="1" y="3" width="20" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.1" fill="none"/>
      <rect x="1" y="3" width="3" height="3"  rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      <rect x="1" y="9" width="3" height="3"  rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      <rect x="18" y="3" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      <rect x="18" y="9" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      {/* Note de musique */}
      <circle cx="8.5"  cy="12.5" r="1.5" fill="currentColor"/>
      <circle cx="13.5" cy="11.5" r="1.5" fill="currentColor"/>
      <line x1="10" y1="12.5" x2="10"  y2="7"   stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="15" y1="11.5" x2="15"  y2="6"   stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="10" y1="7"    x2="15"  y2="6"   stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
    </svg>
  )
})

/** Pellicule + triangle play — pour les coming next */
const IconComingNext = memo(function IconComingNext() {
  return (
    <svg aria-hidden="true" width="22" height="18" viewBox="0 0 22 18" fill="none">
      {/* Pellicule */}
      <rect x="1" y="3" width="20" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.1" fill="none"/>
      <rect x="1" y="3" width="3" height="3"  rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      <rect x="1" y="9" width="3" height="3"  rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      <rect x="18" y="3" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      <rect x="18" y="9" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1" fill="none"/>
      {/* Triangle play */}
      <polygon points="8.5,6.5 8.5,11.5 14,9" fill="currentColor"/>
    </svg>
  )
})

// ─── Ligne de jingle ──────────────────────────────────────────────────────────

const JingleRow = memo(function JingleRow({ program, active, checked, canManage, onSelect, onCheck }: {
  program: Program
  active: boolean
  checked: boolean
  canManage: boolean
  onSelect: (p: Program) => void
  onCheck: (p: Program, checked: boolean, shiftKey: boolean) => void
}) {
  const hasError  = program.mediaAssets?.some(m => ['MISSING', 'INVALID_CODEC'].includes(m.status))
  const isComingNext = program.type === 'COMING_NEXT'
  const isBA = ['BANDE_ANNONCE', 'AVERTISSEMENT', 'BILLBOARD'].includes(program.type)

  const tone = hasError ? 'danger' : isComingNext ? 'coming-next' : isBA ? 'warning' : 'brand'
  return (
    <div className={`jingle-row${checked ? ' is-selected' : ''}${active ? ' is-active' : ''}`}>
      <Checkbox
        className="catalog-selection-checkbox"
        checked={checked}
        disabled={!canManage}
        onClick={event => {
          event.stopPropagation()
          onCheck(program, !checked, event.shiftKey)
        }}
        aria-label={`Sélectionner ${program.title}`}
      />
      <button
        type="button"
        onClick={() => onSelect(program)}
        className="jingle-row__select"
        aria-current={active ? 'true' : undefined}
      >
        {/* Vignette type */}
        <span className={`jingle-row__thumbnail jingle-row__thumbnail--${tone}`}>
          {isComingNext ? <IconComingNext /> : <IconJingle />}
        </span>
        {/* Infos */}
        <span className="jingle-row__copy">
          <span className="jingle-row__title">
            {program.title}
          </span>
          <span className="jingle-row__meta">
            {antennaElementTypeLabel(program)} · {formatDurationShort(program.durationSec)}
          </span>
        </span>
        {/* Badge */}
        <span className="jingle-row__status">
          <span className={`catalog-status-badge ${hasError ? 'catalog-status-badge--danger' : 'catalog-status-badge--success'}`}>
            {hasError ? 'ALERTE' : 'OK'}
          </span>
        </span>
      </button>
    </div>
  )
})

// ─── Modal création d'un jingle ───────────────────────────────────────────────

interface NewJingleModalProps {
  channelId:    string
  elementTypes: AntennaElementType[]
  onCreated:    (p: Program) => void
  onClose:      () => void
}

const NewJingleModal = memo(function NewJingleModal({ channelId, elementTypes, onCreated, onClose }: NewJingleModalProps) {
  const { createProgram } = useProgramActions()
  const [visible,     setVisible]     = useState(false)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [title,       setTitle]       = useState('')
  const [typeId,      setTypeId]      = useState('')
  const [durationSec, setDurationSec] = useState('30')

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  useEffect(() => { setTypeId(prev => prev || elementTypes[0]?.id || '') }, [elementTypes])

  const handleClose = useCallback(() => {
    setVisible(false)
    setTimeout(onClose, 200)
  }, [onClose])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    setError(null)
    try {
      const program = await createProgram({
        channelId,
        title: title.trim(),
        // Le type éditorial porte l'enum technique côté serveur : on n'envoie
        // que l'identifiant, `type` en est déduit.
        antennaElementTypeId: typeId || undefined,
        type: typeId ? undefined : 'JINGLE',
        durationSec: parseInt(durationSec) || 30,
        tags: [],
      })
      onCreated(program)
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [channelId, title, typeId, durationSec, createProgram, onCreated])

  return (
    <ModalShell
      onClose={handleClose}
      size="narrow"
      title="Nouvel élément d’antenne"
      visible={visible}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <ModalField label="Titre *">
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex : Jingle ouverture, Coming Next Soirée…"
              required
              autoFocus
              className="tp-input"
            />
          </ModalField>

          <div className="tp-form-grid">
            <ModalField label="Type">
              <select value={typeId} onChange={e => setTypeId(e.target.value)} className="tp-select">
                {elementTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </ModalField>
            <ModalField label="Durée (secondes)">
              <input
                type="number"
                value={durationSec}
                onChange={e => setDurationSec(e.target.value)}
                min="1"
                className="tp-input"
              />
            </ModalField>
          </div>

          {/* Actions */}
          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">
              Annuler
            </button>
            <button type="submit" disabled={saving || !title.trim()} className="tp-button tp-button--primary" data-state={saving ? 'loading' : undefined}>
              {saving ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})

const ModalField = memo(function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="tp-form-field">
      <span className="tp-field-label">{label}</span>
      {children}
    </label>
  )
})
